import React, { useMemo, useState } from 'react';
import { ChartConfig } from './ChartRenderer';
import { rowsToCsv, triggerDownload, sanitizeFilenameStem } from '../utils/csv';
import { downloadServerCsv } from '../services/backendDuckDB';

/**
 * Universal "Download CSV" button for any chat result with a resolvable data source.
 *
 * Source hierarchy (first match wins):
 *   1. chartConfig.recon_metadata     → server full-outer-join CSV
 *   2. chartConfig.source_table       → server CSV of `SELECT * FROM <table>`
 *   3. chartConfig.source_sql         → server CSV of the exact source SQL
 *   4. data[]                         → client-side CSV of what's on-screen
 *
 * If none of the above are available, the button hides itself.
 */
interface DownloadButtonProps {
  data?: Record<string, unknown>[];
  chartConfig?: ChartConfig;
  backendSessionId?: string;
  /** Human title used to derive the filename; falls back to a timestamped default. */
  title?: string;
}

type Source =
  | { kind: 'recon'; leftTable: string; rightTable: string; joinKey: string; leftLabel?: string; rightLabel?: string }
  | { kind: 'sourceTable'; table: string }
  | { kind: 'sourceSql'; sql: string }
  | { kind: 'clientRows' };

function resolveSource(props: DownloadButtonProps): Source | null {
  const { chartConfig, backendSessionId, data } = props;

  if (chartConfig?.recon_metadata && backendSessionId) {
    const m = chartConfig.recon_metadata;
    return {
      kind: 'recon',
      leftTable: m.left_table,
      rightTable: m.right_table,
      joinKey: m.join_key,
      leftLabel: m.left_label,
      rightLabel: m.right_label,
    };
  }
  if (chartConfig?.source_table && backendSessionId) {
    return { kind: 'sourceTable', table: chartConfig.source_table };
  }
  if (chartConfig?.source_sql && backendSessionId) {
    return { kind: 'sourceSql', sql: chartConfig.source_sql };
  }
  if (data && data.length > 0) {
    return { kind: 'clientRows' };
  }
  return null;
}

const DownloadButton: React.FC<DownloadButtonProps> = (props) => {
  const { data, chartConfig, backendSessionId, title } = props;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const source = useMemo(() => resolveSource(props), [props]);

  if (!source) return null;

  const filenameStem = sanitizeFilenameStem(title || chartConfig?.title || 'data');

  const handleClick = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      if (source.kind === 'clientRows') {
        const csv = rowsToCsv(data || []);
        triggerDownload(csv, `${filenameStem}.csv`);
      } else if (source.kind === 'recon') {
        const result = await downloadServerCsv(
          {
            session_id: backendSessionId!,
            filename_stem: filenameStem,
            recon: {
              left_table: source.leftTable,
              right_table: source.rightTable,
              join_key: source.joinKey,
              left_label: source.leftLabel,
              right_label: source.rightLabel,
            },
          },
          `${filenameStem}.csv`,
        );
        if (!result.ok) setError(result.error || 'Download failed');
      } else {
        const sql =
          source.kind === 'sourceTable'
            ? `SELECT * FROM "${source.table}"`
            : source.sql;
        const result = await downloadServerCsv(
          {
            session_id: backendSessionId!,
            filename_stem: filenameStem,
            sql,
          },
          `${filenameStem}.csv`,
        );
        if (!result.ok) setError(result.error || 'Download failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const labelText = busy ? 'Preparing…' : 'Download CSV';
  const tip =
    source.kind === 'recon'
      ? `Full outer join of ${source.leftTable} and ${source.rightTable} on ${source.joinKey}`
      : source.kind === 'clientRows'
        ? `${(data || []).length} rows (in-browser)`
        : 'Streamed from server';

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        className="download-csv-btn"
        onClick={handleClick}
        disabled={busy}
        title={tip}
        style={{
          padding: '4px 10px',
          fontSize: 12,
          background: 'rgba(255,255,255,0.06)',
          color: '#E5E7EB',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 4,
          cursor: busy ? 'progress' : 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {busy ? '⏳' : '⬇'} {labelText}
      </button>
      {error && (
        <span style={{ color: '#f87171', fontSize: 11 }} title={error}>
          {error.length > 40 ? error.slice(0, 40) + '…' : error}
        </span>
      )}
    </div>
  );
};

export default DownloadButton;
